import axios from "axios"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { changePermitWorkTime } from "modules/permit/api/permitService"
import type { PermitFormState } from "../types"
import { parseTimeParts } from "./usePermitDateTime"
import {
  validateDateRange,
  validateWorkDate,
  validateWorkScheduleRange,
} from "modules/permit/utils/permitHelpers"
import { toast } from "sonner"

const TIME_MINUTE_FALLBACK = "00"

const updateDialogTimeValue = (existingValue: string, updates: Partial<{ hour: string; minute: string }>) => {
  const currentParts = parseTimeParts(existingValue)
  const nextHour = updates.hour ?? currentParts.hour
  const nextMinute = updates.minute ?? currentParts.minute

  if (!nextHour) {
    return ""
  }

  return `${nextHour}:${nextMinute || TIME_MINUTE_FALLBACK}`
}

export type ChangeWorkTimeFieldErrors = {
  startDate: string
  endDate: string
  dateRange: string
  startTime: string
  endTime: string
  reason: string
  form: string
}

const EMPTY_FIELD_ERRORS: ChangeWorkTimeFieldErrors = {
  startDate: "",
  endDate: "",
  dateRange: "",
  startTime: "",
  endTime: "",
  reason: "",
  form: "",
}

type UseChangeWorkTimeFlowParams = {
  tenantId: string
  editingPermitId?: string | null
  submitterUserId?: string | null
  permitData: Pick<PermitFormState, "permitNo" | "startDate" | "endDate" | "startTime" | "endTime">
  navigationState: unknown
}

export const useChangeWorkTimeFlow = ({
  tenantId,
  editingPermitId,
  submitterUserId,
  permitData,
  navigationState,
}: UseChangeWorkTimeFlowParams) => {
  const navigate = useNavigate()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [startTime, setStartTime] = useState("")
  const [endTime, setEndTime] = useState("")
  const [reason, setReason] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<ChangeWorkTimeFieldErrors>(EMPTY_FIELD_ERRORS)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false)
  const [resultStatus, setResultStatus] = useState<"success" | "error">("success")
  const [resultMessage, setResultMessage] = useState("")

  const startTimeParts = useMemo(() => parseTimeParts(startTime), [startTime])
  const endTimeParts = useMemo(() => parseTimeParts(endTime), [endTime])

  const clearErrors = useCallback(() => {
    setError(null)
    setFieldErrors(EMPTY_FIELD_ERRORS)
  }, [])

  const openDialog = useCallback(() => {
    setStartDate(permitData.startDate || "")
    setEndDate(permitData.endDate || permitData.startDate || "")
    setStartTime(permitData.startTime || "")
    setEndTime(permitData.endTime || "")
    setReason("")
    clearErrors()
    setIsDialogOpen(true)
  }, [clearErrors, permitData.endDate, permitData.startDate, permitData.startTime, permitData.endTime])

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open === isDialogOpen) {
        return
      }
      setIsDialogOpen(open)
      if (!open) {
        clearErrors()
      }
    },
    [clearErrors, isDialogOpen],
  )

  const handleResultDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open === isResultDialogOpen) {
        return
      }
      setIsResultDialogOpen(open)
      if (!open && isResultDialogOpen && resultStatus === "success") {
        navigate("/permit-index", { state: navigationState })
      }
    },
    [isResultDialogOpen, navigate, navigationState, resultStatus],
  )

  const handleStartHourChange = useCallback((value: string) => {
    setStartTime((prev) => updateDialogTimeValue(prev, { hour: value }))
  }, [])

  const handleStartMinuteChange = useCallback((value: string) => {
    setStartTime((prev) => updateDialogTimeValue(prev, { minute: value }))
  }, [])

  const handleEndHourChange = useCallback((value: string) => {
    setEndTime((prev) => updateDialogTimeValue(prev, { hour: value }))
  }, [])

  const handleEndMinuteChange = useCallback((value: string) => {
    setEndTime((prev) => updateDialogTimeValue(prev, { minute: value }))
  }, [])

  const validateForm = useCallback(() => {
    const nextErrors: ChangeWorkTimeFieldErrors = { ...EMPTY_FIELD_ERRORS }

    if (!editingPermitId) {
      nextErrors.form =
        "\u0e44\u0e21\u0e48\u0e1e\u0e1a Permit \u0e17\u0e35\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e01\u0e32\u0e23\u0e1b\u0e23\u0e31\u0e1a\u0e40\u0e27\u0e25\u0e32"
    }

    if (!submitterUserId) {
      nextErrors.form =
        "\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e1c\u0e39\u0e49\u0e43\u0e0a\u0e49\u0e07\u0e32\u0e19 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e02\u0e49\u0e32\u0e23\u0e30\u0e1a\u0e1a\u0e43\u0e2b\u0e21\u0e48"
    }

    if (!startDate) {
      nextErrors.startDate =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"
    } else {
      nextErrors.startDate = validateWorkDate(startDate).error ?? ""
    }

    if (!endDate) {
      nextErrors.endDate =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"
    } else {
      nextErrors.endDate = validateWorkDate(endDate).error ?? ""
    }

    if (startDate && endDate) {
      nextErrors.dateRange = validateDateRange(startDate, endDate).error ?? ""
    }

    if (!startTime) {
      nextErrors.startTime =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e27\u0e25\u0e32\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"
    }

    if (!endTime) {
      nextErrors.endTime =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"
    }

    if (!reason.trim()) {
      nextErrors.reason =
        "\u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07"
    }

    if (
      !nextErrors.startDate &&
      !nextErrors.endDate &&
      !nextErrors.dateRange &&
      !nextErrors.startTime &&
      !nextErrors.endTime
    ) {
      const scheduleValidation = validateWorkScheduleRange(startDate, endDate, startTime, endTime)
      if (!scheduleValidation.isValid) {
        const scheduleError = scheduleValidation.error ?? ""
        if (
          scheduleError ===
          "\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e15\u0e49\u0e2d\u0e07\u0e21\u0e32\u0e01\u0e01\u0e27\u0e48\u0e32\u0e40\u0e27\u0e25\u0e32\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"
        ) {
          nextErrors.endTime = scheduleError
        } else if (
          scheduleError ===
          "\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19\u0e15\u0e49\u0e2d\u0e07\u0e44\u0e21\u0e48\u0e40\u0e01\u0e34\u0e19\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14"
        ) {
          nextErrors.dateRange = scheduleError
        } else if (!nextErrors.form) {
          nextErrors.form = scheduleError
        }
      }
    }

    setFieldErrors(nextErrors)
    setError(nextErrors.form || null)

    const hasError = Object.values(nextErrors).some((value) => value.trim().length > 0)
    if (hasError || !editingPermitId || !submitterUserId) {
      return null
    }

    return {
      newWorkStart: `${startDate}T${startTime}`,
      newWorkEnd: `${endDate}T${endTime}`,
    }
  }, [editingPermitId, endDate, endTime, reason, startDate, startTime, submitterUserId])

  const handleSubmit = useCallback(async () => {
    const validated = validateForm()
    if (!validated || !editingPermitId || !submitterUserId) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await changePermitWorkTime(editingPermitId, {
        tenantId,
        permitId: editingPermitId,
        requestedByUserId: submitterUserId,
        newWorkStart: validated.newWorkStart,
        newWorkEnd: validated.newWorkEnd,
        reason: reason.trim(),
      })

      const workflowWarnings =
        response.policyWarnings?.map((warning) => warning.message?.trim()).filter(Boolean).join(" | ") ?? ""
      const emailWarning = response.emailErrorMessage?.trim() ?? ""

      if (emailWarning) {
        toast.error(
          `\u0e2a\u0e48\u0e07\u0e04\u0e33\u0e02\u0e2d\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e41\u0e25\u0e49\u0e27 \u0e41\u0e15\u0e48\u0e2a\u0e48\u0e07\u0e2d\u0e35\u0e40\u0e21\u0e25\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08: ${emailWarning}`,
        )
      }

      setIsDialogOpen(false)
      setResultStatus("success")
      setResultMessage(
        [
          "\u0e2a\u0e48\u0e07\u0e04\u0e33\u0e02\u0e2d\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e41\u0e25\u0e30\u0e40\u0e23\u0e34\u0e48\u0e21 Workflow \u0e43\u0e2b\u0e21\u0e48\u0e41\u0e25\u0e49\u0e27",
          workflowWarnings ? `\u0e04\u0e33\u0e40\u0e15\u0e37\u0e2d\u0e19: ${workflowWarnings}` : "",
          emailWarning ? `\u0e2b\u0e21\u0e32\u0e22\u0e40\u0e2b\u0e15\u0e38\u0e2d\u0e35\u0e40\u0e21\u0e25: ${emailWarning}` : "",
        ]
          .filter(Boolean)
          .join("\n\n"),
      )
      setIsResultDialogOpen(true)
    } catch (caughtError) {
      const message = axios.isAxiosError(caughtError)
        ? ((caughtError.response?.data as { message?: string } | undefined)?.message ??
            caughtError.response?.statusText ??
            caughtError.message ??
            "\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19\u0e44\u0e14\u0e49")
        : "\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e1b\u0e25\u0e35\u0e48\u0e22\u0e19\u0e41\u0e1b\u0e25\u0e07\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19\u0e44\u0e14\u0e49"

      setError(message)
      setFieldErrors((prev) => ({ ...prev, form: message }))
      setResultStatus("error")
      setResultMessage(message)
      setIsResultDialogOpen(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [editingPermitId, reason, submitterUserId, tenantId, validateForm])

  return {
    dialog: {
      open: isDialogOpen,
      permitNumber: permitData.permitNo,
      isSubmitting,
      startDate,
      endDate,
      startHour: startTimeParts.hour,
      startMinute: startTimeParts.minute,
      endHour: endTimeParts.hour,
      endMinute: endTimeParts.minute,
      reason,
      error,
      fieldErrors,
      onOpenChange: handleDialogOpenChange,
      onStartDateChange: setStartDate,
      onEndDateChange: setEndDate,
      onStartHourChange: handleStartHourChange,
      onStartMinuteChange: handleStartMinuteChange,
      onEndHourChange: handleEndHourChange,
      onEndMinuteChange: handleEndMinuteChange,
      onReasonChange: setReason,
      onSubmit: handleSubmit,
    },
    resultDialog: {
      open: isResultDialogOpen,
      status: resultStatus,
      message: resultMessage,
      onOpenChange: handleResultDialogOpenChange,
    },
    actions: {
      openDialog,
    },
    state: {
      isSubmitting,
    },
  }
}
